import { CanvasTexture, RepeatWrapping, SRGBColorSpace } from "three";

// Procedural canvas textures for the katana (blade finish + tsuka wrap +
// tsuba patina). Per the handoff doc: the blade's one small map is a
// roughnessMap (polish variation — no color detail, which fast motion would
// waste anyway) while the near-static hilt carries the color-texture budget.
// Built once (called from useMemo in Katana.tsx), never per-frame.

function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * Blade finish roughnessMap — the hamon as a *polish* feature, the way a real
 * togishi finishes a blade: mirror-bright ha below the temper line, a misty
 * (rougher) wavy hamon band, satin ji above it, and a slightly different
 * polish on the shinogi-ji. With the env map on the steel, the band shows up
 * as a soft "frosted" interruption the reflections sweep across — the
 * reference plate's misty hamon, without a drawn line.
 *
 * Mapping (see bladeGeometry.ts UVs): x = across the blade, 0 at the ha edge
 * → 1 at the mune; y = along the blade length. Values multiply the
 * material's base roughness (three.js reads the green channel).
 */
export function createBladeFinishTexture(): CanvasTexture {
  const w = 64; // across the blade (u)
  const h = 256; // along the blade (v)
  const { canvas, ctx } = makeCanvas(w, h);
  const img = ctx.createImageData(w, h);

  const RIDGE_U = 0.62; // keep in sync with RIDGE_Y_FRAC in bladeGeometry.ts

  for (let y = 0; y < h; y++) {
    const t = y / (h - 1);
    // Gentle midare wobble: two incommensurate sine waves, same spirit as
    // the hamon path in the reference plate (design/Katana Reference.dc.html).
    const center =
      0.24 + 0.05 * Math.sin(t * 21 + 1.2) + 0.025 * Math.sin(t * 47 + 4.0);

    for (let x = 0; x < w; x++) {
      const u = x / (w - 1);

      // Base polish zones: satin ji between hamon and ridge, slightly
      // brighter polish on the shinogi-ji above the ridge.
      let val = u < RIDGE_U ? 0.56 : 0.5;

      // Mirror-polished ha: below the hamon the steel drops toward its
      // smoothest — this is the zone that catches the hard travelling glint.
      const belowBand = 1 - smoothstep(center - 0.1, center - 0.02, u);
      val = val * (1 - belowBand) + 0.3 * belowBand;

      // The misty band itself: a soft roughness bump centred on the wavy
      // temper line. exp falloff keeps it cloud-edged, never a hard stripe.
      const d = (u - center) / 0.045;
      val += 0.42 * Math.exp(-d * d);

      const byte = Math.round(Math.min(1, Math.max(0, val)) * 255);
      const i = (y * w + x) * 4;
      img.data[i + 0] = byte;
      img.data[i + 1] = byte;
      img.data[i + 2] = byte;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);

  const texture = new CanvasTexture(canvas);
  // Linear data (roughness), NOT sRGB — no colorSpace assignment.
  texture.needsUpdate = true;
  return texture;
}

function makeCanvas(
  width: number,
  height: number = width,
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D canvas context unavailable");
  return { canvas, ctx };
}

/** Tsukamaki: criss-cross diamond ito (cord) wrap over same (rayskin) nodules. */
export function createTsukaWrapTexture(): CanvasTexture {
  const size = 256;
  const { canvas, ctx } = makeCanvas(size);

  // Rayskin (same) base — warm tan/grey with a soft vertical gradient.
  const base = ctx.createLinearGradient(0, 0, 0, size);
  base.addColorStop(0, "#8f8676");
  base.addColorStop(0.55, "#b3a892");
  base.addColorStop(1, "#cabf9c");
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, size, size);

  // Rayskin nodules — small cream dots, scattered.
  ctx.fillStyle = "rgba(239, 232, 211, 0.55)";
  for (let i = 0; i < 90; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = 1.2 + Math.random() * 1.8;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // Diamond ito wrap — two families of parallel diagonals forming a
  // criss-cross diamond lattice, tiled across the canvas.
  const tileW = 46;
  const tileH = 78;
  ctx.strokeStyle = "#1c140d";
  ctx.lineWidth = 15;
  ctx.lineCap = "butt";
  const margin = tileH;
  for (let y = -margin; y < size + margin; y += tileH) {
    for (let x = -margin; x < size + margin; x += tileW) {
      ctx.beginPath();
      ctx.moveTo(x, y + tileH);
      ctx.lineTo(x + tileW, y);
      ctx.moveTo(x, y);
      ctx.lineTo(x + tileW, y + tileH);
      ctx.stroke();
    }
  }
  // Sheen — a thinner, lighter stroke offset from one diagonal family, so
  // each cord catches a highlight rather than reading as flat black.
  ctx.strokeStyle = "rgba(58, 42, 26, 0.75)";
  ctx.lineWidth = 3;
  for (let y = -margin; y < size + margin; y += tileH) {
    for (let x = -margin; x < size + margin; x += tileW) {
      ctx.beginPath();
      ctx.moveTo(x, y + tileH - 3);
      ctx.lineTo(x + tileW, y - 3);
      ctx.stroke();
    }
  }

  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.repeat.set(3, 1.5);
  texture.colorSpace = SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

/** Aged iron tsuba face — mottled patina and pitting. */
export function createTsubaPatinaTexture(): CanvasTexture {
  const size = 256;
  const { canvas, ctx } = makeCanvas(size);

  ctx.fillStyle = "#222219";
  ctx.fillRect(0, 0, size, size);

  const cx = size / 2;
  const cy = size / 2;
  const radial = ctx.createRadialGradient(cx * 0.7, cy * 1.4, size * 0.05, cx, cy, size * 0.75);
  radial.addColorStop(0, "#5b5c53");
  radial.addColorStop(0.45, "#3a3b34");
  radial.addColorStop(0.8, "#222219");
  radial.addColorStop(1, "#14140e");
  ctx.fillStyle = radial;
  ctx.fillRect(0, 0, size, size);

  // Patina mottling — a handful of soft, low-opacity blobs.
  const blobs: Array<[number, number, number, number, string, number]> = [
    [0.35, 0.28, 0.18, 0.12, "#4d4e44", 0.4],
    [0.6, 0.55, 0.2, 0.13, "#101009", 0.5],
    [0.28, 0.6, 0.13, 0.09, "#5c5d50", 0.3],
    [0.58, 0.24, 0.12, 0.08, "#191912", 0.45],
  ];
  for (const [bx, by, brx, bry, color, alpha] of blobs) {
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.filter = "blur(6px)";
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.ellipse(bx * size, by * size, brx * size, bry * size, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  // Pitting — small dark and light speckles.
  ctx.globalAlpha = 1;
  ctx.fillStyle = "rgba(13, 13, 8, 0.5)";
  for (let i = 0; i < 40; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = 0.8 + Math.random() * 1.4;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = "rgba(111, 112, 98, 0.35)";
  for (let i = 0; i < 20; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = 0.6 + Math.random() * 1.0;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

/** Habaki (collar) — warm brass with diagonal yasurime file-mark lines. */
export function createHabakiTexture(): CanvasTexture {
  const size = 128;
  const { canvas, ctx } = makeCanvas(size);

  const grad = ctx.createLinearGradient(0, 0, 0, size);
  grad.addColorStop(0, "#6f5a34");
  grad.addColorStop(0.5, "#b79552");
  grad.addColorStop(1, "#e6c37a");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);

  ctx.strokeStyle = "rgba(122, 95, 47, 0.55)";
  ctx.lineWidth = 3;
  for (let x = -size; x < size * 2; x += 14) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x + size * 0.35, size);
    ctx.stroke();
  }

  ctx.strokeStyle = "rgba(246, 224, 164, 0.7)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, 2);
  ctx.lineTo(size, 2);
  ctx.stroke();

  const texture = new CanvasTexture(canvas);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.colorSpace = SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}
